import React, { useEffect, useMemo, useRef, useState } from "react"
import ConfirmModal from "../components/ConfirmModal"
import EmptyState from "../Questions/components/EmptyState"
import useStudentSubjects from "../hooks/useStudentSubjects"
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard"
import NoteDetailModal from "./components/NoteDetailModal"
import NoteComposer from "./components/NoteComposer"
import NotesEmpty from "./components/NotesEmpty"
import NotesGrid from "./components/NotesGrid"
import NotesHeader from "./components/NotesHeader"
import useNoteComposer from "./hooks/useNoteComposer"
import useNotes from "./hooks/useNotes"
import { DEFAULT_NOTE_SUBJECT, type NoteCardViewModel } from "./types"
import "./NotesPage.style.scss"

const NotesPage = () => {
  const list = useNotes()
  const composer = useNoteComposer(list.afterCreate, list.afterUpdate)
  const { subjects } = useStudentSubjects()
  const composerRef = useRef<HTMLDivElement>(null)
  const [pendingDelete, setPendingDelete] = useState<NoteCardViewModel | null>(
    null
  )
  const [openNote, setOpenNote] = useState<NoteCardViewModel | null>(null)
  const unsaved = useUnsavedChangesGuard(composer.isDirty)

  const subjectOptions = useMemo(
    () => [
      { value: DEFAULT_NOTE_SUBJECT, label: DEFAULT_NOTE_SUBJECT },
      ...subjects.map((subject) => ({
        value: subject.title,
        label: subject.title,
      })),
    ],
    [subjects]
  )

  useEffect(() => {
    if (!composer.isOpen) return
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [composer.isOpen, composer.editingId])

  const showEmpty =
    list.hasLoaded && !list.error && list.cards.length === 0 && !composer.isOpen

  const handleNewNote = () => {
    unsaved.requestLeave(() => composer.open())
  }

  const handleEdit = (card: NoteCardViewModel) => {
    if (composer.editingId === card.id && composer.isOpen) return
    unsaved.requestLeave(() => composer.openEdit(card))
  }

  const handleCancelComposer = () => {
    unsaved.requestLeave(() => composer.close())
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    const deletedId = pendingDelete.id
    const ok = await list.removeNote(deletedId)
    if (!ok) return
    setPendingDelete(null)
    if (openNote?.id === deletedId) {
      setOpenNote(null)
    }
    if (composer.editingId === deletedId) {
      composer.close()
    }
  }

  return (
    <div className="notes-page">
      <div className="notes-page__inner">
        <NotesHeader onNewNote={handleNewNote} />

        {composer.isOpen && (
          <div ref={composerRef}>
            <NoteComposer
              draft={composer.draft}
              saving={composer.saving}
              isEditing={Boolean(composer.editingId)}
              subjectOptions={subjectOptions}
              onChange={composer.patch}
              onCancel={handleCancelComposer}
              onSubmit={composer.submit}
            />
          </div>
        )}

        {!list.hasLoaded && list.loading && (
          <EmptyState title="Loading notes…" />
        )}

        {list.hasLoaded && list.error && (
          <EmptyState title="Couldn’t load notes" description={list.error} />
        )}

        {showEmpty && <NotesEmpty onCreate={handleNewNote} />}

        {list.cards.length > 0 && (
          <NotesGrid
            cards={list.cards}
            loading={list.loading && list.hasLoaded}
            loadingMore={list.loadingMore}
            hasMore={list.hasMore}
            remaining={Math.max(
              0,
              (list.pagination?.total ?? 0) - list.cards.length
            )}
            removingId={list.removingId}
            editingId={composer.editingId}
            onLoadMore={() => {
              void list.loadMore()
            }}
            onEdit={handleEdit}
            onRemove={setPendingDelete}
            onOpen={setOpenNote}
          />
        )}
      </div>

      <NoteDetailModal
        note={openNote}
        onClose={() => setOpenNote(null)}
        onEdit={(note) => {
          setOpenNote(null)
          handleEdit(note)
        }}
      />

      <ConfirmModal
        isOpen={unsaved.isConfirmOpen}
        title="Unsaved changes"
        message="There are unsaved changes. Do you still want to continue?"
        confirmLabel="Continue"
        cancelLabel="Stay"
        onCancel={unsaved.stay}
        onConfirm={unsaved.confirmLeave}
      />

      <ConfirmModal
        isOpen={Boolean(pendingDelete)}
        title="Delete note?"
        message={
          pendingDelete
            ? `Delete “${pendingDelete.title}”? This can’t be undone.`
            : "Delete this note? This can’t be undone."
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        isLoading={Boolean(pendingDelete) && list.removingId === pendingDelete?.id}
        onCancel={() => {
          if (!list.removingId) setPendingDelete(null)
        }}
        onConfirm={() => {
          void handleConfirmDelete()
        }}
      />
    </div>
  )
}

export default NotesPage
